import { Component } from 'react'
import { HiShieldCheck } from 'react-icons/hi'
import { toast } from 'react-toastify'
import { appRef } from '../../app'
import { clearSecurityCounters, getUsbFlasherAccess, restartServer } from '../../api/admin'
import { getString } from '../../locale'

type State = {
    restarting: boolean
    clearingCounters: boolean
    usbFlasherCode: string
    usbFlasherRemainingSeconds: number
    loadingUsbFlasherAccess: boolean
}

export class AdminPage extends Component<{}, State> {
    private codeTimer: number | undefined

    state: State = {
        restarting: false,
        clearingCounters: false,
        usbFlasherCode: '',
        usbFlasherRemainingSeconds: 0,
        loadingUsbFlasherAccess: false,
    }

    componentDidMount = async () => {
        appRef.setHeader(<HiShieldCheck className="me-2" size={28} />, () => getString('pagelabels.admin'))
        await this.loadUsbFlasherAccess()
        this.codeTimer = window.setInterval(() => {
            this.setState(prev => {
                if (prev.usbFlasherRemainingSeconds <= 1) {
                    void this.loadUsbFlasherAccess()
                    return { ...prev, usbFlasherRemainingSeconds: 0 }
                }
                return { ...prev, usbFlasherRemainingSeconds: prev.usbFlasherRemainingSeconds - 1 }
            })
        }, 1000)
    }

    componentWillUnmount(): void {
        if (this.codeTimer !== undefined) {
            window.clearInterval(this.codeTimer)
            this.codeTimer = undefined
        }
    }

    loadUsbFlasherAccess = async () => {
        if (this.state.loadingUsbFlasherAccess) return
        this.setState({ loadingUsbFlasherAccess: true })
        try {
            const response = await getUsbFlasherAccess()
            this.setState({
                usbFlasherCode: response.data.code || '',
                usbFlasherRemainingSeconds: Number(response.data.remainingSeconds) || 0,
                loadingUsbFlasherAccess: false,
            })
        } catch (error: any) {
            toast.error(error?.message || getString('admin.usb_flasher_code_error'))
            this.setState({ loadingUsbFlasherAccess: false, usbFlasherRemainingSeconds: 15 })
        }
    }

    onRestartClick = async () => {
        if (this.state.restarting) return
        if (!window.confirm(getString('admin.restart_confirm'))) return

        this.setState({ restarting: true })
        try {
            await restartServer()
            toast.success(getString('admin.restart_sent'))
        } catch (error) {
            toast.error((error as Error).message || getString('admin.restart_failed'))
            this.setState({ restarting: false })
        }
    }

    onClearCountersClick = async () => {
        if (this.state.clearingCounters) return
        if (!window.confirm(getString('admin.clear_security_counters_confirm'))) return
        this.setState({ clearingCounters: true })
        try {
            const result = await clearSecurityCounters()
            toast.success(
                getString('admin.clear_security_counters_done')
                    .replace('{0}', `${result.data.authCountersCleared}`)
                    .replace('{1}', `${result.data.rateLimitCountersCleared}`),
            )
        } catch (error) {
            toast.error((error as Error).message || getString('admin.clear_security_counters_failed'))
        } finally {
            this.setState({ clearingCounters: false })
        }
    }

    render() {
        const { restarting, clearingCounters, usbFlasherCode, usbFlasherRemainingSeconds } = this.state
        return (
            <div className="space-y-6">
                <div className="rounded-xl bg-white dark:bg-gray-800 shadow p-6">
                    <h2 className="text-xl font-semibold dark:text-white mb-2">{getString('admin.server_controls')}</h2>
                    <p className="text-gray-600 dark:text-gray-300 mb-6">{getString('admin.restart_hint')}</p>
                    <div className="flex flex-wrap gap-2">
                        <button
                            disabled={restarting}
                            onClick={this.onRestartClick}
                            className="px-5 py-2 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {restarting ? getString('admin.restarting') : getString('admin.restart_server')}
                        </button>
                        <button
                            disabled={clearingCounters}
                            onClick={this.onClearCountersClick}
                            style={{ backgroundColor: '#d97706' }}
                            className="px-5 py-2 rounded text-white hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {clearingCounters ? getString('admin.clearing_security_counters') : getString('admin.clear_security_counters')}
                        </button>
                    </div>
                    <div className="mt-6 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <div className="text-sm text-gray-600 dark:text-gray-300">{getString('admin.usb_flasher_code_label')}</div>
                                <div className="text-3xl tracking-[0.28em] font-mono font-semibold text-gray-900 dark:text-white">
                                    {usbFlasherCode || '------'}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {getString('admin.usb_flasher_code_refresh_in')}: {usbFlasherRemainingSeconds}s
                                </div>
                            </div>
                            <button onClick={this.loadUsbFlasherAccess} className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">
                                {getString('license.refresh')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}
